import { useEffect, useState } from 'react'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useAppStore } from '@forwardflow/store/appStore'
import { Screen } from '../frame/SetupFrame'
import { useStepFooter } from '../../hooks/useStepFooter'
import {
   ErrorLine,
   IconDot,
   Input,
   LinkButton,
   Row,
   RowCard,
} from '../ui/primitives'
import { Icon } from '../ui/Icon'
import { errorText } from '../../utils/format'
import { FUB_API_KEY_URL } from '../../utils/constants'

/**
 * Step 1: the Follow Up Boss API key.
 *
 * The key is what lets every later screen open pre-filled. It is verified as
 * it is typed; one button both connects and moves on. A brand-new sign-up
 * has no organization yet: it is created here, the key saved, and the record
 * written so the loader reads it back when the organization switches.
 */
export const ConnectStep = () => {
   const { onboardingData, getNextStep, setIsFUBConnected, setOrganizationId } =
      useOnboardingStoreV2()
   const { setCurrentOrganization } = useAppStore()
   const { organizationId, isFUBConnected, email } = onboardingData
   const [key, setKey] = useState('')
   const [checking, setChecking] = useState(false)
   const [valid, setValid] = useState(false)
   const [error, setError] = useState<string | null>(null)
   const [connecting, setConnecting] = useState(false)
   const [help, setHelp] = useState(false)

   // Verify the key as it is typed (debounced).
   useEffect(() => {
      setValid(false)
      setError(null)
      const token = key.trim()
      if (!token) {
         setChecking(false)
         return
      }
      setChecking(true)
      const t = setTimeout(async () => {
         try {
            const response = await OnboardingV2Service.verifyFubApiKey({
               token,
            })
            if (response?.data?.isCrmApiKeyValid === true) setValid(true)
            else if (response?.data?.isCrmApiKeyValid === false)
               setError(
                  "That key didn't work. Check it in Follow Up Boss and paste it again."
               )
            else
               setError(
                  errorText(
                     response,
                     "We couldn't check the key. Try again in a moment."
                  )
               )
         } catch (e) {
            setError(
               errorText(e, "We couldn't check the key. Try again in a moment.")
            )
         } finally {
            setChecking(false)
         }
      }, 500)
      return () => clearTimeout(t)
   }, [key])

   const connect = async () => {
      if (isFUBConnected) {
         getNextStep()
         return
      }
      const token = key.trim()
      if (!token || !valid || checking) return
      setConnecting(true)
      setError(null)
      try {
         let orgId = organizationId
         const isNewOrganization = !orgId
         if (!orgId) {
            if (!email) {
               setError('Sign in again before connecting Follow Up Boss.')
               return
            }
            const started = await OnboardingV2Service.startOnboarding({ email })
            orgId = started?.data?.id || null
            if (!started?.success || !orgId) {
               setError(
                  errorText(
                     started,
                     "We couldn't create your account. Try again."
                  )
               )
               return
            }
         }
         const response = await OnboardingV2Service.submitFubApiKey({
            organizationId: orgId,
            token,
         })
         if (!response?.success) {
            setError(
               errorText(
                  response,
                  "We couldn't connect Follow Up Boss. Try again."
               )
            )
            return
         }
         if (isNewOrganization) {
            const record = useOnboardingStoreV2.getState().onboardingData
            await OnboardingV2Service.updateOnboardingState({
               organizationId: orgId,
               data: {
                  ...record,
                  organizationId: orgId,
                  isFUBConnected: true,
                  currentStep: 'you',
               },
            })
            setOrganizationId(orgId)
            setIsFUBConnected(true)
            getNextStep()
            setCurrentOrganization(orgId)
            return
         }
         setIsFUBConnected(true)
         getNextStep()
      } catch (e) {
         setError(
            errorText(e, "We couldn't connect Follow Up Boss. Try again.")
         )
      } finally {
         setConnecting(false)
      }
   }

   const label = isFUBConnected
      ? 'Continue'
      : connecting
        ? 'Connecting'
        : !key.trim()
          ? 'Paste your key to continue'
          : checking
            ? 'Checking your key'
            : error
              ? 'Try another key'
              : 'Connect Follow Up Boss'

   useStepFooter(label, () => void connect(), {
      disabled: !isFUBConnected && !(valid && !checking && !error),
      loading: connecting,
      hint: isFUBConnected
         ? 'Your team, stages and lead sources are ready.'
         : 'We only read. Nothing in Follow Up Boss is changed.',
      testId: 'connect-fub',
   })

   return (
      <Screen
         stepKey="fub-connect"
         kind="connect"
         title="Connect Follow Up Boss"
         lede="Paste your API key and we read your team, stages, tags and lead sources. Every screen after this comes filled in."
         width="sm"
      >
         {isFUBConnected ? (
            <RowCard testId="fub-connected">
               <Row>
                  <IconDot name="check" tone="green" />
                  <div className="flex-1 min-w-0">
                     <p className="text-[15px] font-semibold">
                        Follow Up Boss is connected
                     </p>
                     <p className="text-[13px] text-[var(--ob-text-2)]">
                        Your team, stages and leads are syncing in the
                        background.
                     </p>
                  </div>
               </Row>
            </RowCard>
         ) : (
            <>
               <Input
                  label="Follow Up Boss API key"
                  value={key}
                  onChange={setKey}
                  placeholder="Paste your key here"
                  autoFocus
                  onEnter={() => void connect()}
                  testId="fub-key"
                  leading={<Icon name="key" size={16} />}
                  size="lg"
                  error={error}
                  success={
                     valid && !checking ? (
                        <span className="inline-flex items-center gap-1">
                           <Icon name="check" size={14} /> Key verified
                        </span>
                     ) : undefined
                  }
                  trailing={checking ? 'Checking' : undefined}
               />
               <div className="mt-4 text-center">
                  <LinkButton
                     onClick={() => setHelp((v) => !v)}
                     testId="fub-help"
                  >
                     {help ? 'Hide the steps' : 'Where do I find my API key?'}
                  </LinkButton>
               </div>
               {help && (
                  <ol className="mt-3 list-decimal pl-9 pr-4 py-4 text-[14px] text-[var(--ob-text-2)] space-y-1.5 rounded-2xl bg-[var(--ob-surface)] border border-[var(--ob-border)]">
                     <li>
                        Open{' '}
                        <a
                           className="text-[var(--ob-light)] font-semibold hover:underline"
                           href={FUB_API_KEY_URL}
                           target="_blank"
                           rel="noreferrer"
                        >
                           Follow Up Boss → Admin → API
                        </a>
                     </li>
                     <li>Click "Create API Key" and name it "Forward Flow".</li>
                     <li>
                        Copy the key and paste it above. You need an admin or
                        owner login.
                     </li>
                  </ol>
               )}
               <p className="mt-4 text-center text-[12px] text-[var(--ob-text-3)] flex items-center justify-center gap-1.5">
                  <Icon name="lock" size={12} /> The key is stored encrypted and
                  can be revoked in Follow Up Boss at any time.
               </p>
            </>
         )}
         {isFUBConnected && error && (
            <ErrorLine testId="fub-error">{error}</ErrorLine>
         )}
      </Screen>
   )
}
